import { createApi } from '@reduxjs/toolkit/query/react';
import { type ReferentPriceType } from '@strata/constants';
import { DateFormats, formatDate, formatNameLastFirst, sortAsc } from '@strata/helper-functions';
import type dayjs from 'dayjs';

import { REFERENCE_API_URL } from 'api/APIBase';
import { handleError, handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export const referenceApi = createApi({
    baseQuery: getBaseQuery(REFERENCE_API_URL),
    reducerPath: 'referenceApi',
    endpoints: (build) => ({
        /** Gets list of payers */
        getPayers: build.query<{ payerId: string; payerName: string }[], void>({
            query: () => 'payers/',
            transformResponse: (response: { payer_id: string; payer_name: string }[]) =>
                response
                    ?.map((payer) => ({
                        payerId: payer.payer_id,
                        payerName: payer.payer_name,
                    }))
                    .sort((a, b) => sortAsc(a.payerName, b.payerName)),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving health plans.',
            ),
        }),
        getPlaceOfServiceMap: build.query<Record<string, string>, void>({
            query: () => 'product_benefit_configs/places',
            transformResponse: (response: { code: string; description: string }[]) =>
                Object.fromEntries(
                    (response ?? []).map((option) => [option.code, option.description]),
                ),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving place of service options.',
            ),
        }),
        getPrescriberDetails: build.query<
            {
                prescriberName: string;
                prescriberNpi: string;
                practice?: string;
                practiceNpi?: string;
            },
            { prescriberNpi: string }
        >({
            query: ({ prescriberNpi }) => `prescribers/${prescriberNpi}`,
            transformResponse: (response: {
                prescriber_npi: string;
                first_name: string;
                last_name: string;
                organization_name?: string;
                practice_npi?: string;
            }) => ({
                prescriberName: formatNameLastFirst(response.last_name, response.first_name),
                prescriberNpi: response.prescriber_npi,
                practice: response.organization_name,
                practiceNpi: response.practice_npi,
            }),
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving prescriber details.', err);
                }
            },
        }),
        getSupplierDetails: build.query<
            {
                supplierNpi: string;
                organizationName?: string;
                address1?: string;
                address2?: string;
                city?: string;
                state?: string;
                zip?: string;
            },
            { supplierNpi: string }
        >({
            query: ({ supplierNpi }) => `suppliers/${supplierNpi}`,
            transformResponse: (response: {
                supplier_npi: string;
                organization_name?: string;
                address_1?: string;
                address_2?: string;
                city?: string;
                state?: string;
                postal_code?: string;
            }) => ({
                supplierNpi: response?.supplier_npi,
                organizationName: response?.organization_name,
                address1: response?.address_1,
                address2: response?.address_2,
                city: response?.city,
                state: response?.state,
                zip: response?.postal_code,
            }),
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving pharmacy details.', err);
                }
            },
        }),
        getProductCodeMap: build.query<
            Record<string, { description: string; codesystem: string }>,
            void
        >({
            query: () => 'productservices/',
            transformResponse: (
                response: { codes: { code: string; codesystem: string; description: string }[] }[],
            ) =>
                Object.fromEntries(
                    (response ?? [])
                        .flatMap((med) => med.codes)
                        .map((code) => [
                            code.code,
                            {
                                description: code.description,
                                codesystem: code.codesystem,
                            },
                        ]),
                ),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving medication details.',
            ),
        }),
        getDiagnosisCodeDescriptionMap: build.query<Record<string, string>, void>({
            query: () => 'diagnoses/codes',
            transformResponse: (
                response: { codesystem: string; code: string; description: string }[],
            ) => Object.fromEntries((response ?? []).map((code) => [code.code, code.description])),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving diagnoses codes.',
            ),
        }),
        getEligiblePricesForOid: build.query<
            { code: string; price: number }[],
            {
                oid: string;
                referentPriceType: ReferentPriceType;
                isBrand: boolean;
                priceSource: string;
                priceDate?: dayjs.ConfigType;
            }
        >({
            query: ({ oid, referentPriceType, isBrand, priceSource, priceDate }) => ({
                url: `prices/oid/${oid}/eligible_prices`,
                params: {
                    referent_price_type: referentPriceType,
                    is_brand: isBrand,
                    price_source: priceSource,
                    price_date: formatDate(priceDate, DateFormats.HYPHEN_DATE),
                },
            }),
            transformResponse: (response: { code: string; price: number }[]) =>
                response?.map((price) => ({ code: price.code, price: price.price })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving unit price info.',
            ),
        }),
    }),
});

export const {
    useGetPayersQuery,
    useGetPlaceOfServiceMapQuery,
    useGetPrescriberDetailsQuery,
    useGetSupplierDetailsQuery,
    useGetProductCodeMapQuery,
    useGetDiagnosisCodeDescriptionMapQuery,
    useGetEligiblePricesForOidQuery,
} = referenceApi;
